// @owners { team: patients }
import { Body, Card, ExternalLink, LgPadding, LoadingEmptyState, getBrand } from '@alto/design-system';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { markWrongUser } from '~shared/actions/verifications';
import { getCompanyPhone } from '~shared/config/getCompanyPhone';
import { COMMS_CHANNEL } from '~shared/constants';
import { getURLParams } from '~shared/helpers/helper';
import { stripNonDigits } from '~shared/helpers/string';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { BackLink } from '~web/components/BackLink';
import { PageBackground, SingleColumnContentWrapper } from '~web/components/Page';

type NotMeRouteParams = {
  readonly verificationType: 'email' | 'phone';
};

function pageText(commsChannel: string, result: boolean | null | undefined) {
  if (result) {
    return <Body>{COMMS_CHANNEL[commsChannel as keyof typeof COMMS_CHANNEL]} successfully removed.</Body>;
  }

  const companyPhone = getCompanyPhone();
  return (
    <Body>
      Please try again or contact support at{' '}
      <ExternalLink href={`tel:${stripNonDigits(companyPhone)}`}>{companyPhone}</ExternalLink> if the problem persists
    </Body>
  );
}

export const NotMe = () => {
  const dispatch = useDispatchShared();
  const { verificationType = '' } = useParams<NotMeRouteParams>();
  const { token } = getURLParams();
  const loading = useSelectorShared((state) => state.ui.loading.markingWrongUserLoading);
  const [isMarkedWrongUser, setIsMarkedWrongUser] = useState(false);
  const onIsMarkedWrongUser = useCallback(async () => {
    const response = await dispatch(markWrongUser(token, verificationType));
    setIsMarkedWrongUser(response);
  }, [dispatch, token, verificationType]);

  useEffect(() => {
    onIsMarkedWrongUser();
  }, [onIsMarkedWrongUser]);

  if (loading) {
    return <LoadingEmptyState />;
  }

  return (
    <PageBackground>
      <SingleColumnContentWrapper>
        <Card>
          <LgPadding>
            <BackLink
              href={`https://${getBrand()}.com`}
              text={`Return to ${getBrand()}.com`}
            />
            {pageText(verificationType, isMarkedWrongUser)}
          </LgPadding>
        </Card>
      </SingleColumnContentWrapper>
    </PageBackground>
  );
};
